import { useMutation } from "@tanstack/react-query";

import {
	activateFusionCrossheadKnife,
	activateFusionCrossheadLatch,
	activateFusionPressureRoller,
	calibrateFusion,
	cycleFusionCrossheadTools,
	cycleFusionTrackTools,
	deactivateFusionCrossheadKnife,
	deactivateFusionCrossheadLatch,
	deactivateFusionPressureRoller,
	feedRollerForward,
} from "@/api/machines/fusion-api";

interface FusionServiceCommand {
	readonly machineId: string;
	readonly command:
		| "toggle-track-roller"
		| "feed-roller-forward"
		| "toggle-cross-head-cut"
		| "toggle-cross-head-latch"
		| "calibrate"
		| "cycle-cross-head-tools"
		| "cycle-track-tools";
	readonly trackNumber?: number;
	readonly isActive?: boolean;
}

const executeFusionServiceCommand = async ({
	machineId,
	command,
	trackNumber,
	isActive,
}: FusionServiceCommand): Promise<void> => {
	switch (command) {
		case "toggle-track-roller":
			if (trackNumber === undefined) {
				throw new Error("A track number is required to toggle the pressure roller");
			}
			await (isActive === true
				? deactivateFusionPressureRoller(machineId, trackNumber)
				: activateFusionPressureRoller(machineId, trackNumber));
			return;
		case "feed-roller-forward":
			await feedRollerForward(machineId, 1);
			return;
		case "toggle-cross-head-cut":
			await (isActive === true ? deactivateFusionCrossheadKnife(machineId) : activateFusionCrossheadKnife(machineId));
			return;
		case "toggle-cross-head-latch":
			await (isActive === true ? deactivateFusionCrossheadLatch(machineId) : activateFusionCrossheadLatch(machineId));
			return;
		case "calibrate":
			await calibrateFusion(machineId);
			return;
		case "cycle-cross-head-tools":
			await cycleFusionCrossheadTools(machineId);
			return;
		case "cycle-track-tools":
			await cycleFusionTrackTools(machineId);
			return;
		default:
			throw new Error(`Unsupported Fusion service command: ${command}`);
	}
};

export const useFusionService = () => {
	const mutation = useMutation({ mutationFn: executeFusionServiceCommand });

	return {
		toggleTrackRoller: (machineId: string, trackNumber: number, isActive: boolean) =>
			mutation.mutateAsync({
				machineId,
				command: "toggle-track-roller",
				trackNumber,
				isActive,
			}),
		feedRollerForward: (machineId: string) => mutation.mutateAsync({ machineId, command: "feed-roller-forward" }),
		toggleCrossHeadCut: (machineId: string, isActive: boolean) =>
			mutation.mutateAsync({ machineId, command: "toggle-cross-head-cut", isActive }),
		toggleCrossHeadLatch: (machineId: string, isActive: boolean) =>
			mutation.mutateAsync({ machineId, command: "toggle-cross-head-latch", isActive }),
		calibrate: (machineId: string) => mutation.mutateAsync({ machineId, command: "calibrate" }),
		cycleCrossHeadTools: (machineId: string) => mutation.mutateAsync({ machineId, command: "cycle-cross-head-tools" }),
		cycleTrackTools: (machineId: string) => mutation.mutateAsync({ machineId, command: "cycle-track-tools" }),
	};
};
